import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useQuery } from 'react-apollo';
import { BasePage, Helmet, SkeletonLoader, StaticContext } from '@sm/webassets';
import { t, defineMessages } from '@sm/intl';
import { LANGUAGES_BY_CODE } from '@sm/locale';
import { ThemeProvider } from 'react-jss';
import { Box } from '@wds/box';
import GetCollectorById from '~app/helpers/gql/GetCollectorById.graphql';
import GetPollingExperiment from '~app/helpers/gql/GetPollingExperiment.graphql';
import theme from '~app/styles/cmsTheme';
import EndpageTemplates from './EndpageTemplates';
import Embedded from './Embedded';
import EmbeddedNonBrandedHeader from '~app/components/EmbeddedNonBrandedHeader';
import NonBranded from './NonBranded';
import RebrandEpForm from './RebrandEpForm';
import RebrandEpFormIntl from './RebrandEpFormIntl';
import Research from './Research';
import PollingEndpage from './PollingEndpage';
import WeightedElementSelector from './utils';
import { GUAC_EXISTING_USER_PAGES, US_GEO_PAGES, GAC_GEO_PAGES } from './constants';

const COPY = defineMessages({
  DEFAULT_THANKS_MESSAGE: {
    id: 'SurveyThanks.thanksMessage',
    defaultMessage: 'Thank you for taking this survey.',
  },
  META_KEYWORDS: {
    id: 'SurveyThanks.metaKeywords',
    defaultMessage:
      'questionnaire, questionnaires, questionaire, questionaires, free online survey, free online surveys',
  },
  THANKS_PAGE_TITLE: {
    id: 'SurveyThanks.title',
    defaultMessage: 'Thank you! Create Your Own Online Survey Now! | SurveyMonkey',
  },
});

function SurveyThanks(): React.ReactElement {
  const {
    pageOptions: {
      canonical,
      collectorId,
      isBasicEndpageGroup,
      isNonBrandedSmParam = false,
      isWhiteLabel = false,
      isGovernment = false,
      isSmReleaseToggles = false,
      isEmbeddedSmParam = false,
      randomNumber,
      languageId: surveyLangId,
    },
    environment: { countryCode },
    userInfo: { isExistingUser },
  } = useContext(StaticContext);

  const [showFooter, setShowFooter] = useState(!isNonBrandedSmParam || !isEmbeddedSmParam);
  const [toReturn, setToReturn] = useState(
    <Box display="flex" flexJustify="center" flexAlign="center" p={5}>
      <SkeletonLoader height={130} width={400}>
        <circle cx="10" cy="20" r="8" />
        <rect x="25" y="15" rx="5" ry="5" width="350" height="10" />
      </SkeletonLoader>
    </Box>
  );
  const isNonEnglish = parseInt(surveyLangId, 10) !== LANGUAGES_BY_CODE.en.id;
  const country = countryCode.toUpperCase();
  const isUSA = country === 'US';
  const isGAC = country === 'GB' || country === 'AU' || country === 'CA';

  let EndPage: React.ReactElement = useMemo(() => <></>, []);
  let potentialPollingEndpage = false;

  const {
    loading: collectorLoading,
    error: collectorError,
    data: collectorData,
  } = useQuery(GetCollectorById, {
    context: {
      targetPath: '/lo-graphql/collector',
      headers: {
        'X-Collector-Id': collectorId,
        'X-Language-Id': surveyLangId,
      },
    },
    variables: {
      collectorId,
      surveyLangId,
    },
    errorPolicy: 'all',
  });

  const { loading: pollingLoading, error: pollingError, data: pollingData } = useQuery(GetPollingExperiment, {});

  const nonBranded = isNonBrandedSmParam || collectorData?.collector?.hideBranding;
  const isEmbedded = isEmbeddedSmParam || collectorData?.collector?.type?.toUpperCase() === 'POPUP';

  useEffect(() => {
    if (collectorError || !collectorData?.collector || isEmbedded || nonBranded) {
      setShowFooter(false);
    }
  }, [collectorData, collectorError, nonBranded, isEmbedded]);

  useEffect(() => {
    if (
      potentialPollingEndpage &&
      !collectorLoading &&
      !collectorError &&
      collectorData?.collector &&
      !pollingLoading &&
      !pollingError &&
      pollingData?.getPollingExperiment?.treatmentName &&
      pollingData?.getPollingExperiment?.experimentName
    ) {
      setToReturn(
        <PollingEndpage
          defaultEndpage={EndPage}
          pollingExperiment={pollingData?.getPollingExperiment?.experimentName}
          pollingTreatment={pollingData?.getPollingExperiment?.treatmentName}
        />
      );
    } else if (!isSmReleaseToggles && (collectorError || !collectorData?.collector)) {
      setToReturn(<NonBranded />);
    } else {
      setToReturn(EndPage);
    }
  }, [
    collectorData,
    collectorError,
    nonBranded,
    isEmbedded,
    pollingData,
    pollingError,
    EndPage,
    collectorLoading,
    isSmReleaseToggles,
    pollingLoading,
    potentialPollingEndpage,
  ]);

  switch (true) {
    case isSmReleaseToggles:
      potentialPollingEndpage = true;
      EndPage = <EndpageTemplates />;
      break;
    case isEmbedded:
      EndPage = nonBranded ? (
        <EmbeddedNonBrandedHeader message={t(COPY.DEFAULT_THANKS_MESSAGE)} icon="thanks" />
      ) : (
        <Embedded />
      );
      break;
    case isGovernment:
      EndPage = <NonBranded />;
      break;
    case isWhiteLabel:
      EndPage = <Research />;
      break;
    case nonBranded:
      EndPage = <NonBranded />;
      break;
    case isNonEnglish:
      potentialPollingEndpage = true;
      EndPage = <RebrandEpFormIntl />;
      break;
    case isBasicEndpageGroup:
      EndPage = <RebrandEpForm />;
      break;
    case isExistingUser && (isUSA || isGAC):
      potentialPollingEndpage = true;
      EndPage = WeightedElementSelector(GUAC_EXISTING_USER_PAGES, randomNumber);
      break;
    case isUSA:
      potentialPollingEndpage = true;
      EndPage = WeightedElementSelector(US_GEO_PAGES, randomNumber);
      break;
    case isGAC:
      potentialPollingEndpage = true;
      EndPage = WeightedElementSelector(GAC_GEO_PAGES, randomNumber);
      break;
    default:
      EndPage = <RebrandEpForm />;
      break;
  }

  return (
    <ThemeProvider theme={theme}>
      <BasePage includeHeader={false} includeFooter={showFooter} pageId="Survey Thanks" legacyWeb="anonweb">
        <Helmet>
          <base target="_top" href="/" />
          <meta name="keywords" content={t(COPY.META_KEYWORDS)} />
          <meta name="description" content={t(COPY.DEFAULT_THANKS_MESSAGE)} />
          <link rel="canonical" href={canonical} />
          <title>{t(COPY.THANKS_PAGE_TITLE)}</title>
        </Helmet>
        {toReturn}
      </BasePage>
    </ThemeProvider>
  );
}

export default SurveyThanks;
